import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { TranslatePipe } from './i18n.service';

@Component({
  selector: 'app-create-job',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="min-h-screen lg:min-h-[calc(100vh-72px)] flex flex-col pb-44 lg:pb-28" style="background:#F7FBFD;">
      <header class="px-5 pt-4 pb-4 flex items-center gap-3 sticky top-0 z-30 text-white shadow-sm lg:hidden"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <button (click)="goBack()" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15">
          <i class="ti ti-arrow-left text-[20px]"></i>
        </button>
        <div class="flex-1 min-w-0">
          <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">{{ 'jobDetails' | t }}</p>
          <h1 class="text-lg font-semibold text-white/95 truncate">{{ 'createJob' | t }}</h1>
        </div>
      </header>

      <main class="flex-1 px-4 pt-5 space-y-4 lg:px-7 lg:pt-6 lg:max-w-[980px]">
        <section class="bg-white rounded-2xl lg:rounded-lg p-4 border border-slate-100">
          <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Job Type</p>
          <div class="space-y-2.5">
            @for (type of jobTypes; track type.value) {
              <button type="button" (click)="selectJobType(type.value)"
                class="w-full p-3 rounded-2xl border flex items-center gap-3 transition-all text-left active:scale-[0.99]"
                [class.border-sky-200]="selectedType === type.value"
                [class.bg-sky-50]="selectedType === type.value && type.value !== 'Emergency'"
                [class.border-rose-200]="selectedType === type.value && type.value === 'Emergency'"
                [class.bg-rose-50]="selectedType === type.value && type.value === 'Emergency'"
                [class.border-slate-100]="selectedType !== type.value"
                [class.bg-white]="selectedType !== type.value">
                <div class="w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0"
                     [class.bg-[#1A6FA6]]="selectedType === type.value && type.value !== 'Emergency'"
                     [class.bg-rose-600]="selectedType === type.value && type.value === 'Emergency'"
                     [class.text-white]="selectedType === type.value"
                     [class.bg-slate-50]="selectedType !== type.value"
                     [class.text-slate-400]="selectedType !== type.value">
                  <i [class]="'ti ' + type.icon"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="font-semibold text-sm text-slate-800">{{ type.label }}</span>
                    @if (type.value === 'Emergency') {
                      <span class="text-[9px] font-medium bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full uppercase">Urgent</span>
                    }
                  </div>
                  <p class="text-xs text-slate-500 mt-0.5 leading-relaxed">{{ type.desc }}</p>
                </div>
                <div class="w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center"
                     [class.border-[#1A6FA6]]="selectedType === type.value && type.value !== 'Emergency'"
                     [class.border-rose-600]="selectedType === type.value && type.value === 'Emergency'"
                     [class.border-slate-200]="selectedType !== type.value">
                  @if (selectedType === type.value) {
                    <div class="w-2.5 h-2.5 rounded-full"
                         [class.bg-[#1A6FA6]]="type.value !== 'Emergency'"
                         [class.bg-rose-600]="type.value === 'Emergency'"></div>
                  }
                </div>
              </button>
            }
          </div>
        </section>

        <section class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Labour Pricing</p>
          <div class="grid grid-cols-2 gap-3">
            <button type="button" (click)="pricingType = 'Hourly'" class="h-11 rounded-xl border-2 text-sm font-semibold"
              [class.border-[#1A6FA6]]="pricingType === 'Hourly'" [class.bg-sky-50]="pricingType === 'Hourly'" [class.text-[#1A6FA6]]="pricingType === 'Hourly'"
              [class.border-slate-100]="pricingType !== 'Hourly'" [class.text-slate-600]="pricingType !== 'Hourly'">Hourly</button>
            <button type="button" (click)="pricingType = 'Fixed'" class="h-11 rounded-xl border-2 text-sm font-semibold"
              [class.border-[#1A6FA6]]="pricingType === 'Fixed'" [class.bg-sky-50]="pricingType === 'Fixed'" [class.text-[#1A6FA6]]="pricingType === 'Fixed'"
              [class.border-slate-100]="pricingType !== 'Fixed'" [class.text-slate-600]="pricingType !== 'Fixed'">Fixed Rate</button>
          </div>
          @if (pricingType === 'Fixed') {
            <label class="block mt-3">
              <span class="block text-xs font-medium text-slate-500 mb-1">Fixed labour charge <span class="text-rose-500">*</span></span>
              <div class="relative"><span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span><input type="number" [(ngModel)]="fixedLabourCharge" min="0" step="0.01" placeholder="0.00" class="w-full h-11 pl-7 pr-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm"></div>
              <p class="mt-2 text-[11px] leading-relaxed text-slate-500">The timer still records actual hours for internal costing. The customer receives this fixed labour charge; materials remain separate.</p>
            </label>
          }
        </section>

        @if (selectedType === 'Emergency') {
          <label class="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 flex items-start gap-3">
            <input type="checkbox" [(ngModel)]="emergencyDoubleRate" class="mt-0.5 rounded border-rose-300 text-rose-600">
            <span><span class="block text-sm font-semibold text-rose-800">Double emergency labour rate</span><span class="block mt-0.5 text-xs leading-relaxed text-rose-600">Charge 2× the hourly rates or fixed labour amount. Materials stay at their normal prices.</span></span>
          </label>
        }

        @if (selectedType === 'Service') {
          <section class="bg-white rounded-2xl p-4 border border-slate-100">
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-xl bg-sky-50 text-[#1A6FA6] flex items-center justify-center shrink-0">
                <i class="ti ti-clipboard-search text-lg"></i>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Inspection Quote</p>
                <p class="text-xs text-slate-500 mt-0.5 leading-relaxed">Link this service job to an earlier inspection quote when this is follow-up work.</p>
              </div>
            </div>

            <div class="mt-3">
              <label class="block text-xs font-medium text-slate-500 mb-1">Linked Inspection <span class="text-slate-400">(Optional)</span></label>
              <select [(ngModel)]="linkedInspectionId"
                class="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
                <option value="">No linked inspection</option>
                @for (inspection of inspectionJobs(); track inspection.id) {
                  <option [value]="inspection.id">{{ inspection.id }} - {{ inspection.customer_name || 'Customer' }}</option>
                }
              </select>
              @if (isLoadingInspections()) {
                <p class="text-xs text-slate-400 mt-2 flex items-center gap-1"><i class="ti ti-loader animate-spin"></i> Loading inspection jobs...</p>
              } @else if (inspectionJobs().length === 0) {
                <p class="text-xs text-slate-400 mt-2">No inspection quotes are available in your assigned jobs yet.</p>
              }
            </div>
          </section>
        }

        <section class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3">{{ 'customer' | t }}</p>
          <div class="space-y-3">
            <div>
              <label class="block text-xs font-medium text-slate-500 mb-1">{{ 'phone' | t }}</label>
              <div class="relative">
                <input type="tel" [(ngModel)]="phone" (input)="onPhoneInput()" placeholder="021 123 4567"
                  class="w-full h-11 px-3 pr-10 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
                @if (isSearchingCustomer()) {
                  <i class="ti ti-loader animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
                } @else {
                  <i class="ti ti-phone absolute right-3 top-1/2 -translate-y-1/2 text-slate-300"></i>
                }
              </div>
              @if (customerResults().length) {
                <div class="mt-2 bg-white border border-slate-100 rounded-2xl overflow-hidden">
                  @for (customer of customerResults(); track customer.id) {
                    <button type="button" (click)="selectCustomer(customer)" class="w-full px-3 py-3 text-left border-b border-slate-50 last:border-0 active:bg-slate-50">
                      <p class="text-sm font-semibold text-slate-800">{{ customer.name }}</p>
                      <p class="text-xs text-slate-500 mt-0.5">{{ customer.phone }} @if (customer.address) { <span> - {{ customer.address }}</span> }</p>
                    </button>
                  }
                </div>
              }
            </div>

            <div>
              <label class="block text-xs font-medium text-slate-500 mb-1">{{ 'customer' | t }} <span class="text-rose-500">*</span></label>
              <input type="text" [(ngModel)]="customerName" placeholder="Customer name"
                class="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
            </div>
          </div>
        </section>

        <section class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3">{{ 'locationDetails' | t }}</p>
          <label class="block text-xs font-medium text-slate-500 mb-1">{{ 'address' | t }} <span class="text-rose-500">*</span></label>
          <input #siteAddressInput type="text" [(ngModel)]="address" (ngModelChange)="onAddressManualChange()" placeholder="12 Example Street, Auckland"
            class="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
          @if (mapsKey()) {
            <p class="text-[11px] text-slate-400 mt-2">Select an Auckland Google Maps address when available, or type the site address manually.</p>
          }
        </section>

        <section class="bg-white rounded-2xl p-4 border border-slate-100">
          <div class="flex items-center justify-between gap-3">
            <div>
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Scheduling</p>
              <p class="text-xs text-slate-500 mt-0.5">Create now or set a future appointment.</p>
            </div>
            <button type="button" (click)="scheduleLater.set(!scheduleLater())"
                    class="w-12 h-7 rounded-full p-0.5 transition-all"
                    [class.bg-[#1A6FA6]]="scheduleLater()"
                    [class.bg-slate-200]="!scheduleLater()">
              <span class="block w-6 h-6 rounded-full bg-white transition-transform"
                    [class.translate-x-5]="scheduleLater()"></span>
            </button>
          </div>
          @if (scheduleLater()) {
            <div class="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label class="block text-xs font-medium text-slate-500 mb-1">Date</label>
                <input type="date" [(ngModel)]="scheduledDate"
                  class="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
              </div>
              <div>
                <label class="block text-xs font-medium text-slate-500 mb-1">Time</label>
                <input type="time" [(ngModel)]="scheduledTime"
                  class="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
              </div>
            </div>
          }
        </section>

        @if (canAssignWorkers()) {
          <section class="bg-white rounded-2xl p-4 border border-slate-100">
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Assign Field Team</p>
                <p class="text-xs text-slate-500 mt-0.5">You will be assigned automatically. Select anyone else who should work on this job.</p>
              </div>
              @if (isLoadingWorkers()) {
                <i class="ti ti-loader animate-spin text-slate-400 mt-1"></i>
              }
            </div>

            @if (assignableWorkers().length) {
              <div class="mt-3 space-y-2">
                @for (worker of assignableWorkers(); track worker.id) {
                  <label class="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 cursor-pointer">
                    <input type="checkbox"
                      [checked]="isWorkerSelected(worker.id)"
                      (change)="toggleWorker(worker.id, $any($event.target).checked)"
                      class="rounded border-slate-300 text-[#1A6FA6] focus:ring-[#1A6FA6]">
                    <span class="w-8 h-8 rounded-full bg-sky-100 text-[#1A6FA6] flex items-center justify-center text-xs font-bold shrink-0">
                      {{ workerInitials(worker.name) }}
                    </span>
                    <span class="min-w-0">
                      <span class="block text-sm font-semibold text-slate-800 truncate">{{ worker.name }}</span>
                      <span class="block text-[11px] text-slate-500">{{ worker.role === 'Worker' ? 'Field Worker' : worker.role }}</span>
                    </span>
                  </label>
                }
              </div>
            } @else if (!isLoadingWorkers()) {
              <p class="text-xs text-slate-400 mt-3">No other active field workers are available.</p>
            }
          </section>
        }

        <section class="bg-white rounded-2xl p-4 border border-slate-100">
          <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3">{{ 'description' | t }}</p>
          <textarea [(ngModel)]="description" placeholder="Briefly describe the issue or work required" rows="4"
            class="w-full px-3 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm resize-none leading-relaxed"></textarea>
        </section>

        @if (validationError) {
          <div class="flex items-start gap-3 bg-rose-50 border border-rose-100 rounded-2xl px-4 py-3">
            <i class="ti ti-alert-circle text-rose-500 text-xl shrink-0"></i>
            <p class="text-sm font-medium text-rose-700">{{ validationError }}</p>
          </div>
        }

        <section class="bg-sky-50 border border-sky-100 rounded-2xl px-4 py-3">
          <p class="text-xs text-sky-700 leading-relaxed">
            After creation, open the job and use the Start button there. The pre-start safety acknowledgement will be recorded before the timer begins.
          </p>
        </section>
      </main>

      <div class="fixed bottom-[76px] left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-100 p-4 z-30">
        <button (click)="submitJob()" [disabled]="isSaving()"
          class="w-full h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60 text-white"
          [class.bg-rose-600]="selectedType === 'Emergency'"
          [class.bg-[#102C41]]="selectedType !== 'Emergency'">
          @if (isSaving()) {
            <i class="ti ti-loader animate-spin text-lg"></i> {{ 'loading' | t }}
          } @else if (selectedType === 'Inspection') {
            <i class="ti ti-clipboard-search text-lg"></i> {{ 'createJob' | t }}
          } @else if (selectedType === 'Emergency') {
            <i class="ti ti-alert-triangle text-lg"></i> {{ 'createJob' | t }}
          } @else {
            <i class="ti ti-tool text-lg"></i> {{ 'createJob' | t }}
          }
        </button>
      </div>
    </div>
  `
})
export class CreateJobComponent implements OnDestroy, OnInit {
  private api = inject(ApiService);
  private router = inject(Router);
  @ViewChild('siteAddressInput') siteAddressInput?: ElementRef<HTMLInputElement>;

  customerName = '';
  phone = '';
  address = '';
  description = '';
  pricingType: 'Hourly' | 'Fixed' = 'Hourly';
  fixedLabourCharge: number | null = null;
  emergencyDoubleRate = false;
  selectedType = 'Service';
  linkedInspectionId = '';
  scheduledDate = '';
  scheduledTime = '';
  validationError = '';
  isSaving = signal(false);
  scheduleLater = signal(false);
  isSearchingCustomer = signal(false);
  customerResults = signal<any[]>([]);
  inspectionJobs = signal<any[]>([]);
  isLoadingInspections = signal(false);
  assignableWorkers = signal<any[]>([]);
  isLoadingWorkers = signal(false);
  selectedWorkerIds: number[] = [];
  mapsKey = signal('');
  selectedCustomerId: number | null = null;
  addressPlaceId = '';
  addressLatitude: number | null = null;
  addressLongitude: number | null = null;

  private searchTimer: ReturnType<typeof setTimeout> | undefined;
  private isDestroyed = false;
  private addressAutocompleteInitialized = false;

  jobTypes = [
    {
      value: 'Inspection',
      label: 'Inspection / Quote Only',
      icon: 'ti-clipboard-search',
      desc: 'Inspect the site, estimate materials and prepare a quote.'
    },
    {
      value: 'Service',
      label: 'Service Job',
      icon: 'ti-tool',
      desc: 'Create a standard plumbing job assigned to you.'
    },
    {
      value: 'Emergency',
      label: 'Emergency Call',
      icon: 'ti-alert-triangle',
      desc: 'Urgent on-site issue that needs immediate attention.'
    },
  ];

  ngOnInit() {
    this.loadInspectionJobs();
    this.loadMapSettings();
    if (this.canAssignWorkers()) this.loadFieldWorkers();
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  goBack() {
    this.router.navigate(['/']);
  }

  selectJobType(type: string) {
    this.selectedType = type;
    if (type !== 'Emergency') this.emergencyDoubleRate = false;
    if (type !== 'Service') {
      this.linkedInspectionId = '';
    } else if (this.inspectionJobs().length === 0) {
      this.loadInspectionJobs();
    }
  }

  canAssignWorkers(): boolean {
    const user = this.api.currentUser() || {};
    return String(user.role || '') === 'Admin' && !!user.has_worker_access;
  }

  loadFieldWorkers() {
    this.isLoadingWorkers.set(true);
    const currentUserId = Number(this.api.currentUser()?.id || 0);
    this.api.getFieldWorkers().subscribe({
      next: (res: any) => {
        this.isLoadingWorkers.set(false);
        const workers = res?.success && Array.isArray(res.data?.workers) ? res.data.workers : [];
        this.assignableWorkers.set(workers.filter((worker: any) => Number(worker.id) !== currentUserId));
      },
      error: () => {
        this.isLoadingWorkers.set(false);
        this.assignableWorkers.set([]);
      }
    });
  }

  isWorkerSelected(workerId: number | string): boolean {
    return this.selectedWorkerIds.includes(Number(workerId));
  }

  toggleWorker(workerId: number | string, selected: boolean) {
    const id = Number(workerId);
    if (!id) return;
    this.selectedWorkerIds = selected
      ? Array.from(new Set([...this.selectedWorkerIds, id]))
      : this.selectedWorkerIds.filter(value => value !== id);
  }

  workerInitials(name: string): string {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : (parts[0]?.slice(0, 2) || 'FW')).toUpperCase();
  }

  loadInspectionJobs() {
    this.isLoadingInspections.set(true);
    this.api.getJobs().subscribe({
      next: (res: any) => {
        this.isLoadingInspections.set(false);
        if (res.success && res.data?.jobs) {
          this.inspectionJobs.set(
            res.data.jobs.filter((job: any) => job.type === 'Inspection' && ['Quote Sent', 'Approved', 'Completed'].includes(job.status))
          );
        } else {
          this.inspectionJobs.set([]);
        }
      },
      error: () => {
        this.isLoadingInspections.set(false);
        this.inspectionJobs.set([]);
      }
    });
  }

  onPhoneInput() {
    this.selectedCustomerId = null;
    const query = this.phone.trim();
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (query.length < 3) {
      this.customerResults.set([]);
      return;
    }
    this.searchTimer = setTimeout(() => {
      this.isSearchingCustomer.set(true);
      this.api.searchCustomerByPhone(query).subscribe({
        next: (res: any) => {
          this.isSearchingCustomer.set(false);
          this.customerResults.set(res.success ? (res.data?.customers || []) : []);
        },
        error: () => {
          this.isSearchingCustomer.set(false);
          this.customerResults.set([]);
        }
      });
    }, 300);
  }

  selectCustomer(customer: any) {
    this.selectedCustomerId = Number(customer.id) || null;
    this.customerName = customer.name || this.customerName;
    this.phone = customer.phone || this.phone;
    this.address = customer.address || this.address;
    this.addressPlaceId = customer.address_place_id || '';
    this.addressLatitude = customer.latitude !== undefined && customer.latitude !== null ? Number(customer.latitude) : null;
    this.addressLongitude = customer.longitude !== undefined && customer.longitude !== null ? Number(customer.longitude) : null;
    this.customerResults.set([]);
  }

  onAddressManualChange() {
    this.addressPlaceId = '';
    this.addressLatitude = null;
    this.addressLongitude = null;
  }

  validate(): boolean {
    if (!this.customerName.trim()) {
      this.validationError = 'Customer name is required.';
      return false;
    }
    if (!this.address.trim()) {
      this.validationError = 'Site address is required.';
      return false;
    }
    if (this.selectedType === 'Emergency' && !this.description.trim()) {
      this.validationError = 'Please add a short description for emergency jobs.';
      return false;
    }
    if (this.scheduleLater() && (!this.scheduledDate || !this.scheduledTime)) {
      this.validationError = 'Please choose both date and time for scheduled jobs.';
      return false;
    }
    if (this.pricingType === 'Fixed' && (this.fixedLabourCharge === null || Number(this.fixedLabourCharge) < 0)) {
      this.validationError = 'Enter a valid fixed labour charge.';
      return false;
    }
    this.validationError = '';
    return true;
  }

  submitJob() {
    if (!this.validate() || this.isSaving()) return;

    this.isSaving.set(true);
    const payload: any = {
      description: this.description.trim() || 'No description provided.',
      job_type: this.selectedType,
      type: this.selectedType,
      priority: this.selectedType === 'Emergency' ? 'Urgent' : 'Routine',
      address: this.address.trim(),
      address_place_id: this.addressPlaceId,
      latitude: this.addressLatitude,
      longitude: this.addressLongitude,
      customer_name: this.customerName.trim(),
      customer_id: this.selectedCustomerId,
      pricing_type: this.pricingType,
      fixed_labour_charge: this.pricingType === 'Fixed' ? Number(this.fixedLabourCharge || 0) : null,
      emergency_double_rate: this.selectedType === 'Emergency' && this.emergencyDoubleRate ? 1 : 0,
      linked_inspection_id: this.selectedType === 'Service' && this.linkedInspectionId ? this.linkedInspectionId : null,
      creation_source: 'field_app',
      assigned_worker_ids: this.canAssignWorkers() ? this.selectedWorkerIds : [],
      contacts: [{
        name: this.customerName.trim(),
        phone: this.normalizeNzPhone(this.phone),
        email: '',
        type: 'Customer'
      }]
    };

    if (this.scheduleLater()) {
      payload.scheduled_date = `${this.scheduledDate} ${this.scheduledTime}:00`;
    }

    this.api.createJob(payload).subscribe({
      next: (res: any) => {
        this.isSaving.set(false);
        if (res.success) {
          const jobId = res.data?.id || res.data?.job_id;
          showToast('success', 'Job created successfully.');
          if (jobId) {
            this.router.navigate(['/job', jobId]);
          } else {
            this.router.navigate(['/schedule']);
          }
        } else {
          this.validationError = res.message || 'Failed to create job. Please try again.';
        }
      },
      error: () => {
        this.isSaving.set(false);
        this.validationError = 'Network error. Please check your connection and try again.';
      }
    });
  }

  private normalizeNzPhone(raw: string): string {
    const cleaned = raw.trim().replace(/[^\d+]/g, '');
    if (!cleaned) return '';
    if (cleaned.startsWith('+64')) return cleaned;
    if (cleaned.startsWith('64')) return `+${cleaned}`;
    if (cleaned.startsWith('0')) return `+64${cleaned.substring(1)}`;
    return `+64${cleaned}`;
  }

  private loadMapSettings() {
    this.api.getCompanySettings().subscribe({
      next: (res: any) => {
        const key = String(res?.data?.google_maps_browser_api_key || '').trim();
        this.mapsKey.set(key);
        if (key) this.loadGoogleMapsScript(key);
      }
    });
  }

  private loadGoogleMapsScript(key: string) {
    if (typeof window === 'undefined') return;
    if ((window as any).google?.maps?.places) {
      this.initAddressAutocomplete();
      return;
    }
    const existing = document.getElementById('google-maps-js');
    if (existing) {
      existing.addEventListener('load', () => this.initAddressAutocomplete());
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => this.initAddressAutocomplete();
    document.head.appendChild(script);
  }

  private initAddressAutocomplete() {
    if (this.isDestroyed || this.addressAutocompleteInitialized) return;
    if (!this.siteAddressInput?.nativeElement || !(window as any).google?.maps?.places) {
      setTimeout(() => this.initAddressAutocomplete(), 100);
      return;
    }

    const google = (window as any).google;
    const autocomplete = new google.maps.places.Autocomplete(this.siteAddressInput.nativeElement, {
      componentRestrictions: { country: 'nz' },
      bounds: this.aucklandBounds(),
      strictBounds: true,
      fields: ['formatted_address', 'geometry', 'place_id', 'address_components']
    });
    this.addressAutocompleteInitialized = true;

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const lat = place.geometry?.location ? Number(place.geometry.location.lat()) : null;
      const lng = place.geometry?.location ? Number(place.geometry.location.lng()) : null;
      if (!this.isInAucklandBounds(lat, lng)) {
        this.addressPlaceId = '';
        this.addressLatitude = null;
        this.addressLongitude = null;
        this.validationError = 'Please select an Auckland address.';
        return;
      }
      this.address = place.formatted_address || this.address;
      this.addressPlaceId = place.place_id || '';
      this.addressLatitude = lat;
      this.addressLongitude = lng;
      this.validationError = '';
    });
  }

  private aucklandBounds() {
    return {
      north: -36.35,
      south: -37.35,
      east: 175.45,
      west: 174.15
    };
  }

  private isInAucklandBounds(lat: number | null, lng: number | null): boolean {
    if (lat === null || lng === null) return false;
    const bounds = this.aucklandBounds();
    return lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
  }
}
